import { MktSection } from "../components/marketing";
import { Seo } from "../lib/seo";

/**
 * Guardian & younger-user information page.
 *
 * Child-safety route (persona P01 Emil). This page is the calm, plain-language
 * boundary a younger visitor is directed to instead of independent signup. It
 * does NOT collect any data and does NOT offer a purchase or account-creation
 * path aimed at a child.
 *
 * NOTE (truthfulness): this page provides guidance only. Server-side age
 * eligibility enforcement (blocking under-minimum-age account creation and
 * purchase at the API) is tracked as a separate, still-required control — see
 * reports/persona-simulation/latest/CHILD_SAFETY_REPORT.md. This page must not
 * be described as "child-safe" on its own.
 */
export function Guardians(): JSX.Element {
  return (
    <>
      <Seo
        title="Guardians & younger users"
        description="How parents, guardians, teachers and community leaders can set up and manage ODIN for younger community members — and the age requirements for using ODIN with Discord."
        path="/guardians"
      />

      <MktSection
        kicker="For younger users"
        title="You need an adult to continue"
        lead="ODIN connects to Discord, and Discord has a minimum age. If you are younger than that, please ask a parent, guardian, teacher, or community leader to set up and manage the ODIN account with you. You do not need to enter your name, age, or any personal details on this page."
        id="younger-users"
      >
        <div className="mkt-prose">
          <p>
            ODIN is a tool for running a Discord community. It is meant to be set up and looked after
            by an adult who is responsible for the community. If you want to use ODIN, the safest and
            simplest way is to ask a grown-up you trust to help you.
          </p>
          <p>
            You will not be asked to pay for anything here, and you should never share your real name,
            address, phone number, or a password with anyone who asks for it online.
          </p>
        </div>
      </MktSection>

      <MktSection
        kicker="For parents, guardians & community leaders"
        title="Setting up ODIN on behalf of a younger member"
        lead="If you are a parent, guardian, teacher, youth worker, or community leader, you can create and own the ODIN account and manage the Discord server on behalf of younger members."
        id="for-adults"
      >
        <div className="mkt-prose">
          <p>
            <strong>Age requirements.</strong> ODIN works with Discord, so everyone using it must meet
            Discord&rsquo;s own minimum age requirement for their country. ODIN is not a substitute for
            Discord&rsquo;s rules, and we ask you not to help anyone create a Discord account below that
            minimum age.
          </p>
          <p>
            <strong>Who should own the account.</strong> The ODIN account, the billing (if any), and the
            connection to the Discord server should be owned and managed by a responsible adult. Younger
            members can take part in the community on Discord without needing their own ODIN account.
          </p>
          <p>
            <strong>Safety by design.</strong> ODIN stages every structural change as a dry run and never
            applies anything to a server without an explicit approval, which keeps an adult in control of
            what happens to the community.
          </p>
          <p>
            <strong>Questions?</strong> Contact us at{" "}
            <a href="mailto:post@okmedia.live">post@okmedia.live</a> — we are happy to help you set things
            up safely.
          </p>
        </div>
      </MktSection>
    </>
  );
}
